/**
 * Reader-study timer pill — displays elapsed `mm:ss` since the rad
 * clicked Start. Pauses when the parent says `paused` (typically
 * driven by `useIdleTimer.isIdle`); the pill renders dim and frozen
 * while paused so the rad can see why time isn't accruing.
 *
 * Rendered in the right-panel header next to the "N / total" counter.
 */
import { useEffect, useState } from "react";

interface StudyTimerPillProps {
  /** `performance.now()` value when the timer started. null = not started;
   *  pill renders idle. Monotonic (not Unix wall-clock) so a mid-case
   *  system-clock adjustment doesn't jump the displayed elapsed. */
  startedAt?: number | null;
  /** Controlled mode: show these seconds instead of counting from `startedAt`.
   *  null = not known yet (renders --:--). Used by the segmentation study. */
  elapsedSeconds?: number | null;
  /** When true, freeze the displayed value (idle detection / interruption). */
  paused?: boolean;
  /** Untimed acclimation case — render a static "not timed" pill instead of
   *  a clock so the reader knows this case isn't counted. */
  isPractice?: boolean;
}

export const StudyTimerPill: React.FC<StudyTimerPillProps> = ({
  startedAt = null,
  elapsedSeconds,
  paused = false,
  isPractice = false,
}) => {
  const controlled = elapsedSeconds !== undefined;
  const [countedMs, setCountedMs] = useState(0);

  useEffect(() => {
    if (controlled || isPractice || startedAt === null || paused) return;
    const tick = () => setCountedMs(performance.now() - startedAt);
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [controlled, startedAt, paused, isPractice]);
  const elapsedMs = controlled ? (elapsedSeconds ?? 0) * 1000 : countedMs;
  const unknown = controlled ? elapsedSeconds === null : startedAt === null;

  if (isPractice) {
    return (
      <span
        data-testid="study-timer-pill"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono text-fg-muted border border-border-strong bg-bg-2/50"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-fg-faint" />
        Practice · not timed
      </span>
    );
  }

  if (unknown) {
    return (
      <span
        data-testid="study-timer-pill"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono text-fg-muted border border-border-strong bg-bg-2/50"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-fg-faint" />
        --:--
      </span>
    );
  }

  const totalSec = Math.floor(elapsedMs / 1000);
  const mm = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  const tone = paused
    ? "text-fg-dim border-border-strong bg-surface-2"
    : "text-signal-text-tint border-signal-stroke bg-signal-tint";

  return (
    <span
      data-testid="study-timer-pill"
      data-paused={paused ? "true" : "false"}
      className={
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-mono border " + tone
      }
    >
      <span
        className={"w-1.5 h-1.5 rounded-full " + (paused ? "bg-fg-dim" : "bg-signal animate-pulse")}
      />
      {mm}:{ss}
    </span>
  );
};
